// Database-backed settings service with localStorage fallback
import { analytics } from '../utils/analytics';

interface Setting {
    key: string;
    value: any;
    category?: string;
    description?: string;
    type?: 'string' | 'number' | 'boolean' | 'json';
}

interface PromoCode {
    id?: number;
    code: string;
    description: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    isSeasonal: boolean;
    seasonName?: string;
    utmTrigger?: string;
    bannerText?: string;
    bannerColor?: string;
    validFrom: Date;
    validTo: Date;
    usageLimit?: number;
    usageCount?: number;
    isActive: boolean;
}

interface PainPoint {
    id?: number;
    name: string;
    description: string;
    category?: string;
    is_active: boolean;
}

interface BookingOption {
    id?: number;
    name: string;
    description: string;
    duration: number;
    category?: string;
    is_active: boolean;
}

interface TwilioConfig {
    account_sid?: string;
    auth_token?: string;
    phone_number?: string;
    webhook_url?: string;
    is_enabled: boolean;
    message_templates?: {
        onsite?: string;
        phone?: string;
    };
}

interface PromotionalBanner {
    id: number;
    title: string;
    message: string;
    discount_percentage: number;
    background_color: string;
    text_color: string;
    is_active: boolean;
}

interface PricingConfig {
    id: number;
    painPointName: string;
    painPointDisplayName: string;
    pricePerSqm: number;
    description: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface PricingConfigResponse {
    pricing: PricingConfig[];
    travelHourlyRate: number;
}

interface BookingRules {
    cutoffHour: number;
    sameDayLeadTime: number;
    nextDayLeadTime: number;
    allowWeekends: boolean;
    timezone: string;
}

interface ServiceTimeConfig {
    startTime: string; // Format: "HH:MM" (24-hour)
    endTime: string;   // Format: "HH:MM" (24-hour)
    displayMode: 'windows' | 'slots'; // 'windows' = AM/PM selection, 'slots' = specific time slots
    blockDuration: number; // Duration in minutes (applies to both windows and slots)
    isEnabled: boolean;
}

interface EnhancedBookingRules extends BookingRules {
    serviceTypes: {
        jobs: ServiceTimeConfig;
        quotes: ServiceTimeConfig;
        officeQuotes: ServiceTimeConfig;
    };
    debugLogging: {
        enabled: boolean;
        logLevel: 'minimal' | 'detailed' | 'verbose';
        includeTiming: boolean;
        includeAvailability: boolean;
    };
}

class SettingsService {
    private apiBaseUrl: string;
    private useLocalStorage: boolean;

    constructor() {
        this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
        // Only use localStorage when explicitly enabled, not when API_BASE_URL is missing
        // In development, we want to use the proxy to the backend, not localStorage
        this.useLocalStorage = process.env.REACT_APP_USE_LOCAL_STORAGE === 'true';
    }

    private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
        // Use localStorage fallback when enabled
        if (this.useLocalStorage) {
            return this.getDefaultResponse(endpoint);
        }

        try {
            const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
                ...options,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error(`Settings API request failed for ${endpoint}:`, error);
            analytics.trackError('settings_api_request_failed', `${endpoint}: ${error instanceof Error ? error.message : 'Unknown error'}`);
            
            // Return default values for critical settings when API is unavailable
            return this.getDefaultResponse(endpoint);
        }
    }

    private getDefaultResponse(endpoint: string): any {
        // Provide sensible defaults when API is not available
        switch (endpoint) {
            case '/settings/seasonal_promotions_enabled':
                return { value: false };
            case '/settings/promotional_banners':
                return [];
            case '/settings/glass_minimum_threshold':
                return { value: 4 };
            case '/admin/pricing':
                return {
                    pricing: [
                        { id: 1, painPointName: 'standard', painPointDisplayName: 'Standard Tint', pricePerSqm: 45, description: 'Basic window tinting', isActive: true }
                    ],
                    travelHourlyRate: 52
                };
            default:
                return { success: true, settings: [], promoCodes: [], painPoints: [], bookingOptions: [] };
        }
    }

    // Settings Management
    async getAllSettings(): Promise<Setting[]> {
        const response = await this.makeRequest('/settings');
        return response.settings || [];
    }

    async getSetting(key: string): Promise<any> {
        const response = await this.makeRequest(`/settings/${key}`);
        return response.success && response.setting ? response.setting.value : null;
    }

    async setSetting(key: string, value: any, category?: string, description?: string): Promise<boolean> {
        const response = await this.makeRequest(`/settings/${key}`, {
            method: 'POST',
            body: JSON.stringify({ value, category, description }),
        });

        if (response.success) {
            analytics.track('setting_updated', 'settings_service', { key, category });
        }

        return response.success;
    }

    async deleteSetting(key: string): Promise<boolean> {
        const response = await this.makeRequest(`/settings/${key}`, {
            method: 'DELETE',
        });

        if (response.success) {
            analytics.track('setting_deleted', 'settings_service', { key });
        }

        return response.success;
    }

    // Promo Codes Management
    async getPromoCodes(): Promise<PromoCode[]> {
        const response = await this.makeRequest('/promo-codes');
        const rawPromoCodes = response.promoCodes || [];
        
        // Convert snake_case to camelCase
        return rawPromoCodes.map((promo: any) => ({
            id: promo.id,
            code: promo.code,
            description: promo.description,
            discountType: promo.discountType,
            discountValue: promo.discountValue,
            isSeasonal: promo.isSeasonal,
            seasonName: promo.seasonName,
            utmTrigger: promo.utmTrigger,
            bannerText: promo.bannerText,
            bannerColor: promo.bannerColor,
            validFrom: new Date(promo.validFrom),
            validTo: new Date(promo.validTo),
            usageLimit: promo.usageLimit,
            usageCount: promo.usageCount,
            isActive: promo.isActive,
        }));
    }

    async getPromoCode(code: string): Promise<PromoCode | null> {
        try {
            const response = await this.makeRequest(`/promo-codes/${code}`);
            if (response.success) {
                const promo = response.promoCode;
                return {
                    id: promo.id,
                    code: promo.code,
                    description: promo.description,
                    discountType: promo.discountType,
                    discountValue: promo.discountValue,
                    isSeasonal: promo.isSeasonal,
                    seasonName: promo.seasonName,
                    utmTrigger: promo.utmTrigger,
                    bannerText: promo.bannerText,
                    bannerColor: promo.bannerColor,
                    validFrom: new Date(promo.validFrom),
                    validTo: new Date(promo.validTo),
                    usageLimit: promo.usageLimit,
                    usageCount: promo.usageCount,
                    isActive: promo.isActive,
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    }

    async createPromoCode(promoCode: Omit<PromoCode, 'id'>): Promise<PromoCode | null> {
        const response = await this.makeRequest('/promo-codes', {
            method: 'POST',
            body: JSON.stringify(promoCode),
        });

        if (response.success) {
            const promo = response.promoCode;
            analytics.track('promo_code_created', 'settings_service', { code: promo.code });
            return {
                id: promo.id,
                code: promo.code,
                description: promo.description,
                discountType: promo.discountType,
                discountValue: promo.discountValue,
                isSeasonal: promo.isSeasonal,
                seasonName: promo.seasonName,
                utmTrigger: promo.utmTrigger,
                bannerText: promo.bannerText,
                bannerColor: promo.bannerColor,
                validFrom: new Date(promo.validFrom),
                validTo: new Date(promo.validTo),
                usageLimit: promo.usageLimit,
                usageCount: promo.usageCount,
                isActive: promo.isActive,
            };
        }

        return null;
    }

    async updatePromoCode(id: number, promoCode: Partial<PromoCode>): Promise<boolean> {
        const response = await this.makeRequest(`/promo-codes/${id}`, {
            method: 'PUT',
            body: JSON.stringify(promoCode),
        });

        if (response.success) {
            analytics.track('promo_code_updated', 'settings_service', { id });
        }

        return response.success;
    }

    async deletePromoCode(id: number): Promise<boolean> {
        const response = await this.makeRequest(`/promo-codes/${id}`, {
            method: 'DELETE',
        });

        if (response.success) {
            analytics.track('promo_code_deleted', 'settings_service', { id });
        }

        return response.success;
    }

    // Pain Points Management
    async getPainPoints(): Promise<PainPoint[]> {
        const response = await this.makeRequest('/pain-points');
        return response.painPoints || [];
    }

    async createPainPoint(painPoint: Omit<PainPoint, 'id'>): Promise<PainPoint | null> {
        const response = await this.makeRequest('/pain-points', {
            method: 'POST',
            body: JSON.stringify(painPoint),
        });

        if (response.success) {
            analytics.track('pain_point_created', 'settings_service', { name: painPoint.name });
            return response.painPoint;
        }

        return null;
    }

    async updatePainPoint(id: number, painPoint: Partial<PainPoint>): Promise<boolean> {
        const response = await this.makeRequest(`/pain-points/${id}`, {
            method: 'PUT',
            body: JSON.stringify(painPoint),
        });

        if (response.success) {
            analytics.track('pain_point_updated', 'settings_service', { id });
        }

        return response.success;
    }

    async deletePainPoint(id: number): Promise<boolean> {
        const response = await this.makeRequest(`/pain-points/${id}`, {
            method: 'DELETE',
        });

        if (response.success) {
            analytics.track('pain_point_deleted', 'settings_service', { id });
        }

        return response.success;
    }

    // Booking Options Management
    async getBookingOptions(): Promise<BookingOption[]> {
        const response = await this.makeRequest('/booking-options');
        return response.bookingOptions || [];
    }

    async createBookingOption(option: Omit<BookingOption, 'id'>): Promise<BookingOption | null> {
        const response = await this.makeRequest('/booking-options', {
            method: 'POST',
            body: JSON.stringify(option),
        });

        if (response.success) {
            analytics.track('booking_option_created', 'settings_service', { name: option.name });
            return response.bookingOption;
        }

        return null;
    }

    async updateBookingOption(id: number, option: Partial<BookingOption>): Promise<boolean> {
        const response = await this.makeRequest(`/booking-options/${id}`, {
            method: 'PUT',
            body: JSON.stringify(option),
        });

        if (response.success) {
            analytics.track('booking_option_updated', 'settings_service', { id });
        }

        return response.success;
    }

    async deleteBookingOption(id: number): Promise<boolean> {
        const response = await this.makeRequest(`/booking-options/${id}`, {
            method: 'DELETE',
        });

        if (response.success) {
            analytics.track('booking_option_deleted', 'settings_service', { id });
        }

        return response.success;
    }

    // Twilio Configuration
    async getTwilioConfig(): Promise<TwilioConfig | null> {
        console.log('🔍 SettingsService: getTwilioConfig() called');
        try {
            // Get Twilio settings from the twilio_config table directly
            console.log('🔍 SettingsService: Making request to /settings/twilio-config');
            const response = await this.makeRequest('/settings/twilio-config');
            console.log('🔍 SettingsService: Response received:', response);
            
            if (response.success && response.config) {
                console.log('✅ SettingsService: Returning config:', response.config);
                return response.config;
            }
            console.log('❌ SettingsService: No config in response');
            return null;
        } catch (error) {
            console.error('❌ SettingsService: Failed to get Twilio configuration:', error);
            return null;
        }
    }

    async updateTwilioConfig(config: TwilioConfig): Promise<boolean> {
        try {
            const response = await this.makeRequest('/settings/twilio-config', {
                method: 'POST',
                body: JSON.stringify(config)
            });
            if (response.success) {
                analytics.track('twilio_config_updated', 'settings_service');
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to update Twilio configuration:', error);
            analytics.trackError('twilio_config_update_failed', error instanceof Error ? error.message : 'Unknown error');
            return false;
        }
    }

    // Promotional Banners
    async getPromotionalBanners(): Promise<PromotionalBanner[]> {
        try {
            // Get promotional banner settings from the settings API
            const response = await this.makeRequest('/settings?category=banners');
            if (response.success && response.settings) {
                // Convert settings to promotional banners format
                // This might need adjustment based on how banners are stored
                return response.settings.map((setting: any) => ({
                    id: setting.id,
                    title: setting.key,
                    message: setting.description || '',
                    discount_percentage: 0, // These might need to be stored differently
                    background_color: '#F59E0B',
                    text_color: '#FFFFFF',
                    is_active: setting.value || false
                }));
            }
            return [];
        } catch (error) {
            console.error('Failed to get promotional banners:', error);
            return [];
        }
    }

    // Calendar View Settings
    async getCalendarViewSetting(): Promise<boolean> {
        try {
            const value = await this.getSetting('calendar_view_enabled');
            return value !== null ? value : true; // Default to enabled
        } catch (error) {
            console.error('Error getting calendar view setting:', error);
            return true; // Default to enabled
        }
    }

    async setCalendarViewSetting(enabled: boolean): Promise<boolean> {
        return this.setSetting(
            'calendar_view_enabled',
            enabled,
            'ui',
            'Enable calendar view in booking interface'
        );
    }

    // UTM Promo Code Activation
    async activateUTMPromoCode(utmCode: string): Promise<PromoCode | null> {
        try {
            const response = await this.makeRequest(`/promo-codes/utm/${utmCode}`, {
                method: 'POST',
            });

            if (response.success && response.promoCode) {
                const promo = response.promoCode;
                analytics.track('utm_promo_activated', 'settings_service', { 
                    code: promo.code,
                    utmTrigger: utmCode 
                });
                
                return {
                    id: promo.id,
                    code: promo.code,
                    description: promo.description,
                    discountType: promo.discountType,
                    discountValue: promo.discountValue,
                    isSeasonal: promo.isSeasonal,
                    seasonName: promo.seasonName,
                    utmTrigger: promo.utmTrigger,
                    bannerText: promo.bannerText,
                    bannerColor: promo.bannerColor,
                    validFrom: new Date(promo.validFrom),
                    validTo: new Date(promo.validTo),
                    usageLimit: promo.usageLimit,
                    usageCount: promo.usageCount,
                    isActive: promo.isActive,
                };
            }
            return null;
        } catch (error) {
            console.error('Failed to activate UTM promo code:', error);
            return null;
        }
    }

    // Feature Toggles
    async getPromoCodeToggle(): Promise<boolean> {
        const value = await this.getSetting('promo_codes_enabled');
        return value !== null ? value : true; // Default to enabled
    }

    async setPromoCodeToggle(enabled: boolean): Promise<boolean> {
        return this.setSetting(
            'promo_codes_enabled',
            enabled,
            'promotions',
            'Enable or disable promo code functionality'
        );
    }

    async getSeasonalPromotionsToggle(): Promise<boolean> {
        const value = await this.getSetting('seasonal_promotions_enabled');
        return value !== null ? value : true; // Default to enabled
    }

    async setSeasonalPromotionsToggle(enabled: boolean): Promise<boolean> {
        return this.setSetting(
            'seasonal_promotions_enabled',
            enabled,
            'promotions',
            'Enable or disable seasonal promotional banners'
        );
    }





    // Pricing Configuration
    async getPricingConfig(): Promise<PricingConfigResponse | null> {
        try {
            // Get pricing data from the admin/pricing endpoint
            const response = await this.makeRequest('/admin/pricing');
            if (response.success && response.pricing) {
                // Convert the database format to the expected interface format
                const pricing = response.pricing.map((item: any) => ({
                    id: item.id,
                    painPointName: item.pain_point_name.toLowerCase().replace(/\s+/g, '_'), // Convert to snake_case
                    painPointDisplayName: item.pain_point_name,
                    pricePerSqm: parseFloat(item.price_per_sqm),
                    description: item.description,
                    isActive: item.is_active,
                    createdAt: item.created_at,
                    updatedAt: item.updated_at
                }));

                return {
                    pricing,
                    travelHourlyRate: response.travelHourlyRate || 52
                };
            }
            return null;
        } catch (error) {
            console.error('Failed to get pricing configuration:', error);
            return null;
        }
    }

    async updatePricingConfig(pricing: PricingConfig[], travelHourlyRate: number): Promise<boolean> {
        try {
            // Update travel hourly rate in settings
            const response = await this.setSetting('travel_hourly_rate', travelHourlyRate, 'pricing', 'Hourly rate for travel calculation');
            
            if (response) {
                analytics.track('pricing_config_updated', 'settings_service', {
                    pricingCount: pricing.length,
                    travelRate: travelHourlyRate
                });
            }

            return response;
        } catch (error) {
            console.error('Failed to update pricing configuration:', error);
            return false;
        }
    }

    // Form Webhook Settings
    async getFormWebhookEnabled(): Promise<boolean> {
        const value = await this.getSetting('form_webhook_enabled');
        return value !== null ? value : false; // Default to disabled
    }

    async setFormWebhookEnabled(enabled: boolean): Promise<boolean> {
        return this.setSetting(
            'form_webhook_enabled',
            enabled,
            'webhooks',
            'Enable webhook trigger when customer completes contact form'
        );
    }

    async getFormWebhookUrl(): Promise<string> {
        const value = await this.getSetting('form_webhook_url');
        return value !== null ? value : 'https://alphaair.com.au/form-success.php'; // Default URL
    }

    async setFormWebhookUrl(url: string): Promise<boolean> {
        return this.setSetting(
            'form_webhook_url',
            url,
            'webhooks',
            'Webhook URL to trigger when customer completes contact form'
        );
    }

    // Trigger form completion webhook (with session management)
    async triggerFormWebhook(): Promise<boolean> {
        try {
            // Check if webhook is enabled
            const enabled = await this.getFormWebhookEnabled();
            if (!enabled) {
                console.log('Form webhook is disabled');
                return false;
            }

            // Check session storage to prevent multiple triggers
            const sessionKey = 'form_webhook_triggered';
            if (sessionStorage.getItem(sessionKey)) {
                console.log('Form webhook already triggered in this session');
                return false;
            }

            // Get webhook URL
            const webhookUrl = await this.getFormWebhookUrl();
            if (!webhookUrl) {
                console.error('No webhook URL configured');
                return false;
            }

            // Trigger webhook
            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    timestamp: new Date().toISOString(),
                    event: 'form_completion'
                })
            });

            if (response.ok) {
                // Mark as triggered in session storage
                sessionStorage.setItem(sessionKey, 'true');
                analytics.track('form_webhook_triggered', 'settings_service', {
                    url: webhookUrl,
                    timestamp: new Date().toISOString()
                });
                console.log('Form webhook triggered successfully');
                return true;
            } else {
                console.error('Form webhook failed:', response.status, response.statusText);
                return false;
            }
        } catch (error) {
            console.error('Failed to trigger form webhook:', error);
            analytics.trackError('form_webhook_error', `${error instanceof Error ? error.message : 'Unknown error'}`);
            return false;
        }
    }

    // Booking Rules Management
    async getBookingRules(): Promise<BookingRules> {
        try {
            const response = await this.makeRequest('/simpro/settings/booking-rules');
            
            if (response?.success && response?.rules) {
                return {
                    cutoffHour: response.rules.cutoff_hour?.value || 16,
                    sameDayLeadTime: response.rules.same_day_lead_time?.value || 1,
                    nextDayLeadTime: response.rules.next_day_lead_time?.value || 2,
                    allowWeekends: response.rules.allow_weekends?.value || false,
                    timezone: response.rules.timezone?.value || 'Australia/Melbourne'
                };
            }
            
            throw new Error('Invalid response format');
        } catch (error) {
            console.error('Failed to fetch booking rules:', error);
            // Return default values
            return {
                cutoffHour: 16,
                sameDayLeadTime: 1,
                nextDayLeadTime: 2,
                allowWeekends: false,
                timezone: 'Australia/Melbourne'
            };
        }
    }

    async getEnhancedBookingRules(): Promise<EnhancedBookingRules> {
        try {
            const response = await this.makeRequest('/simpro/settings/enhanced-booking-rules');
            
            if (response?.success && response?.rules) {
                const basicRules = await this.getBookingRules();
                
                return {
                    ...basicRules,
                    serviceTypes: {
                        jobs: response.rules.service_jobs || {
                            startTime: "08:00",
                            endTime: "17:00",
                            displayMode: "slots",
                            blockDuration: 240, // 4 hours in minutes
                            isEnabled: true
                        },
                        quotes: response.rules.service_quotes || {
                            startTime: "09:00",
                            endTime: "16:00",
                            displayMode: "windows",
                            blockDuration: 120, // 2 hours in minutes
                            isEnabled: true
                        },
                        officeQuotes: response.rules.service_office_quotes || {
                            startTime: "09:00",
                            endTime: "12:00",
                            displayMode: "windows",
                            blockDuration: 60, // 1 hour in minutes
                            isEnabled: true
                        }
                    },
                    debugLogging: response.rules.debug_logging || {
                        enabled: false,
                        logLevel: "minimal",
                        includeTiming: true,
                        includeAvailability: false
                    }
                };
            }
            
            throw new Error('Invalid response format');
        } catch (error) {
            console.error('Failed to fetch enhanced booking rules:', error);
            const basicRules = await this.getBookingRules();
            
            // Return default enhanced values
            return {
                ...basicRules,
                serviceTypes: {
                    jobs: {
                        startTime: "08:00",
                        endTime: "17:00",
                        displayMode: "slots",
                        blockDuration: 240, // 4 hours in minutes
                        isEnabled: true
                    },
                    quotes: {
                        startTime: "09:00",
                        endTime: "16:00",
                        displayMode: "windows",
                        blockDuration: 120, // 2 hours in minutes
                        isEnabled: true
                    },
                    officeQuotes: {
                        startTime: "09:00",
                        endTime: "12:00",
                        displayMode: "windows",
                        blockDuration: 60, // 1 hour in minutes
                        isEnabled: true
                    }
                },
                debugLogging: {
                    enabled: false,
                    logLevel: "minimal",
                    includeTiming: true,
                    includeAvailability: false
                }
            };
        }
    }

    async updateBookingRules(rules: Partial<BookingRules>): Promise<boolean> {
        try {
            const response = await this.makeRequest('/simpro/settings/booking-rules', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(rules)
            });

            if (response?.success) {
                analytics.track('booking_rules_updated', 'settings_service', rules);
                return true;
            }
            
            throw new Error(response?.error || 'Unknown error');
        } catch (error) {
            console.error('Failed to update booking rules:', error);
            analytics.trackError('booking_rules_update_error', `${error instanceof Error ? error.message : 'Unknown error'}`);
            return false;
        }
    }

    async updateEnhancedBookingRules(rules: Partial<EnhancedBookingRules>): Promise<boolean> {
        try {
            const response = await this.makeRequest('/simpro/settings/enhanced-booking-rules', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(rules)
            });

            if (response?.success) {
                analytics.track('enhanced_booking_rules_updated', 'settings_service', {
                    hasServiceTypes: !!rules.serviceTypes,
                    hasDebugLogging: !!rules.debugLogging,
                    serviceTypesCount: rules.serviceTypes ? Object.keys(rules.serviceTypes).length : 0
                });
                return true;
            }
            
            throw new Error(response?.error || 'Unknown error');
        } catch (error) {
            console.error('Failed to update enhanced booking rules:', error);
            analytics.trackError('enhanced_booking_rules_update_error', `${error instanceof Error ? error.message : 'Unknown error'}`);
            return false;
        }
    }

    async getNextBookingDate(): Promise<string> {
        try {
            const response = await this.makeRequest('/simpro/next-booking-date');
            
            if (response?.success && response?.nextBookingDate) {
                return response.nextBookingDate;
            }
            
            throw new Error('Invalid response format');
        } catch (error) {
            console.error('Failed to fetch next booking date:', error);
            // Return tomorrow as fallback
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow.toISOString().split('T')[0];
        }
    }
}

export const settingsService = new SettingsService();
export type { Setting, PromoCode, PainPoint, BookingOption, TwilioConfig, PromotionalBanner, PricingConfig, PricingConfigResponse, BookingRules };
